import { useAuthStore } from '@/stores/authStore';
import { useRows } from '@/hooks/useLive';
import { db } from '@/lib/db';
import { newId } from '@/lib/ids';
import { queue } from '@/lib/sync';
import { siteRuns } from '@/lib/operations';
import { QUARRY_SITE_ID } from '@/lib/fleet';
import type {
  PlantOperation, PlantProduct, PlantProduction, PlantRejectReason, PlantStockMove, Site, UserRole,
} from '@/types/models';

/**
 * The Brick Plant and Pre-cast modules (0063). The two run on one engine;
 * `op` says which. Mirrors app.plant_site_role / plant_list_editor: the
 * server enforces, this only decides what the screens offer.
 *
 * Linked to branches (0069): a site runs it when ticked under
 * Administration → Branches. Who: administrators, and users with the module
 * ticked on their own list (never a role default), at the sites they work:
 *   quarry manager / admin   every site running it that they may see
 *   branch people            their own branch(es), by their role
 *   the quarry's people      a plant at the quarry, by their quarry role
 * Within a site: manager / admin capture, correct any, count, targets;
 * supervisor capture, correct any, count; operator capture, correct own.
 */
export const PLANT: Record<PlantOperation, { label: string; path: string; unit: string; noun: string }> = {
  brick: { label: 'Brick Plant', path: '/brick', unit: 'pieces', noun: 'bricks, blocks and pavers' },
  precast: { label: 'Pre-cast', path: '/precast', unit: 'pieces', noun: 'fencing, palisade and kerbs' },
};

export interface PlantAccess {
  canUse: boolean;
  /** Sites running the operation where this user works (local mirror). */
  sites: Site[];
  roleAt: (site: string | null | undefined) => UserRole | null;
  canCapture: (site: string | null | undefined) => boolean;
  canCorrect: (site: string | null | undefined, createdBy: string | null | undefined) => boolean;
  canEditLists: boolean;
  /** Adjustments and stock counts (0064): a supervisor or manager at the site. */
  canCount: (site: string | null | undefined) => boolean;
  /** Targets (0065): a manager or administrator at the site, with the module. */
  canLead: (site: string | null | undefined) => boolean;
  userId: string | null;
}

export function usePlantAccess(op: PlantOperation): PlantAccess {
  const profile = useAuthStore((s) => s.profile);
  const access = useAuthStore((s) => s.access);
  useAuthStore((s) => s.selectedBranch);
  const all = useRows<Site>('sites', 'name');
  const members = useAuthStore.getState().memberBranches();
  const hasModule = useAuthStore.getState().hasModule(op);
  const role = profile?.is_active === false ? null : profile?.role ?? null;
  // Per user (0066, kept by 0069): administrators, or the module ticked.
  const allowed = role === 'admin' || hasModule;
  const isBranch = !!profile?.home_site_id;
  const visible = (site: string) => !access || access.all_sites || access.sites.includes(site);
  // Mirrors app.plant_site_role (0069).
  const roleAt = (site: string | null | undefined): UserRole | null => {
    if (!allowed || !site || !role) return null;
    if (role === 'admin') return visible(site) ? role : null;
    if (site === QUARRY_SITE_ID) return access ? (access.sees_quarry ? role : null) : (isBranch ? null : role);
    if (members.includes(site)) return role;
    return role === 'manager' && !isBranch && visible(site) ? role : null;
  };
  const sites = (all ?? []).filter((s) => !s.deleted_at && s.is_active && siteRuns(s, op) && roleAt(s.id) != null);
  const lead = (site: string | null | undefined) => roleAt(site) === 'manager' || roleAt(site) === 'admin';
  return {
    // Only where a site runs it: a module no site of theirs runs stays out of the menu.
    canUse: allowed && sites.length > 0,
    sites,
    roleAt,
    canCapture: (site) => roleAt(site) != null,
    canCorrect: (site, createdBy) => lead(site) || roleAt(site) === 'supervisor'
      || (roleAt(site) != null && !!createdBy && createdBy === profile?.id),
    // Shared lists (0069): quarry management or an administrator.
    canEditLists: allowed && (role === 'admin' || (role === 'manager' && !isBranch && (access?.sees_quarry ?? true))),
    canCount: (site) => lead(site) || roleAt(site) === 'supervisor',
    canLead: lead,
    userId: profile?.id ?? null,
  };
}

/** Pieces → the product's other measure (m² for pavers, m for kerbs), if set. */
export function measureOf(p: Pick<PlantProduct, 'per_m2' | 'per_m'>, pieces: number): { value: number; unit: string } | null {
  if (p.per_m2) return { value: pieces / Number(p.per_m2), unit: 'm²' };
  if (p.per_m) return { value: pieces / Number(p.per_m), unit: 'm' };
  return null;
}

export const reasonsFor = (reasons: PlantRejectReason[] | undefined, op: PlantOperation) =>
  (reasons ?? []).filter((r) => r.is_active && (!r.operation || r.operation === op));

/* ------------------------------------------------------------------ */
/* Capture: one row per site, day and product. Field capture, so it    */
/* goes to the local mirror and the outbox and works without signal.  */
/* ------------------------------------------------------------------ */

export interface DayLine {
  product_id: string;
  made: number | null;
  rejected: number | null;
  reject_reason_id: string | null;
  reject_note: string | null;
  notes: string | null;
}

/** The table's and trigger's rules, so a slip is caught at the plant. */
export function lineProblem(l: DayLine, product: PlantProduct, reasons: PlantRejectReason[]): string | null {
  const made = Number(l.made ?? 0);
  const rej = Number(l.rejected ?? 0);
  if (!Number.isInteger(made) || made < 0) return 'Made must be a whole number';
  if (!Number.isInteger(rej) || rej < 0) return 'Rejected must be a whole number';
  if (made === 0 && rej === 0) return 'Enter what was made';
  if (rej > made) return 'More rejected than made';
  if (rej > 0) {
    if (!l.reject_reason_id) return 'Say why they were rejected';
    const r = reasons.find((x) => x.id === l.reject_reason_id);
    if (r?.requires_note && !(l.reject_note ?? '').trim()) return `"${r.name}" needs a note`;
  }
  if (!product) return 'Unknown product';
  return null;
}

export async function saveLine(
  site: string, op: PlantOperation, day: string, l: DayLine, existing: PlantProduction | undefined, userId: string | null,
): Promise<PlantProduction> {
  const rej = Number(l.rejected ?? 0);
  const row = {
    ...(existing ?? {}),
    id: existing?.id ?? newId(),
    site_id: site,
    operation: op,
    production_date: day,
    product_id: l.product_id,
    made: Number(l.made ?? 0),
    rejected: rej,
    good: Number(l.made ?? 0) - rej,
    reject_reason_id: rej > 0 ? l.reject_reason_id : null,
    reject_note: rej > 0 ? (l.reject_note?.trim() || null) : null,
    notes: l.notes?.trim() || null,
    updated_at: new Date().toISOString(),
    created_by: existing?.created_by ?? userId,
    created_at: existing?.created_at ?? new Date().toISOString(),
    deleted_at: null,
  } as PlantProduction;
  await db.plant_production.put(row);
  const payload: Record<string, unknown> = { ...row };
  for (const k of ['updated_at', 'created_at', 'created_by', 'updated_by', 'deleted_at', 'good']) delete payload[k];
  await queue({ entity: 'plant_production', entity_id: row.id, op: 'upsert', payload });
  return row;
}

export async function removeLine(id: string): Promise<void> {
  await db.plant_production.delete(id);
  await queue({ entity: 'plant_production', entity_id: id, op: 'delete', payload: null });
}

/* ------------------------------------------------------------------ */
/* Stock moves (0064): dispatch, return, adjustment                    */
/* ------------------------------------------------------------------ */

export interface MoveDraft {
  id?: string;
  site_id: string;
  move_date: string;
  kind: PlantStockMove['kind'];
  product_id: string | null;
  pieces: number | null;
  customer_id: string | null;
  delivery_note: string | null;
  vehicle: string | null;
  reason: string | null;
  notes: string | null;
}

export function validateMove(d: MoveDraft, today: string): Record<string, string> {
  const e: Record<string, string> = {};
  if (!d.move_date) e.move_date = 'Date is required';
  else if (d.move_date > today) e.move_date = 'Not in the future';
  if (!d.product_id) e.product_id = 'Choose the product';
  const n = Number(d.pieces ?? 0);
  if (!Number.isInteger(n)) e.pieces = 'Whole pieces';
  else if (d.kind === 'adjustment' ? n === 0 : n <= 0) e.pieces = d.kind === 'adjustment' ? 'Enter + or − pieces' : 'Enter the pieces';
  if (d.kind === 'dispatch' && !d.customer_id) e.customer_id = 'Choose the customer';
  if (d.kind === 'adjustment' && !(d.reason ?? '').trim()) e.reason = 'Say why';
  return e;
}

export async function saveMove(
  op: PlantOperation, d: MoveDraft, existing: PlantStockMove | undefined, userId: string | null,
): Promise<PlantStockMove> {
  const row = {
    ...(existing ?? {}),
    id: d.id ?? existing?.id ?? newId(),
    site_id: d.site_id,
    operation: op,
    move_date: d.move_date,
    kind: d.kind,
    product_id: d.product_id!,
    pieces: Number(d.pieces),
    customer_id: d.kind === 'adjustment' ? null : d.customer_id,
    delivery_note: d.delivery_note?.trim() || null,
    vehicle: d.vehicle?.trim().toUpperCase() || null,
    reason: d.kind === 'adjustment' ? (d.reason?.trim() || null) : null,
    notes: d.notes?.trim() || null,
    updated_at: new Date().toISOString(),
    created_by: existing?.created_by ?? userId,
    created_at: existing?.created_at ?? new Date().toISOString(),
    deleted_at: null,
  } as PlantStockMove;
  await db.plant_stock_moves.put(row);
  const payload: Record<string, unknown> = { ...row };
  for (const k of ['updated_at', 'created_at', 'created_by', 'updated_by', 'deleted_at']) delete payload[k];
  await queue({ entity: 'plant_stock_move', entity_id: row.id, op: 'upsert', payload });
  return row;
}

export async function removeMove(id: string): Promise<void> {
  await db.plant_stock_moves.delete(id);
  await queue({ entity: 'plant_stock_move', entity_id: id, op: 'delete', payload: null });
}

/** What plant_stock() returns per product. */
export interface StockLine {
  product_id: string; code: string; name: string; category: string | null;
  per_pallet: number | null; per_m2: number | null; per_m: number | null; curing_days: number; is_active: boolean;
  on_hand: number; curing: number; available: number; made: number; dispatched: number; returned: number; adjusted: number;
  made_mtd: number; dispatched_mtd: number; last_count: string | null; short: boolean;
}
export interface StockSite {
  site_id: string; name: string; code: string;
  totals: { on_hand: number; curing: number; available: number; made_mtd: number; dispatched_mtd: number; short: number };
  products: StockLine[];
}
export interface StockData { operation: PlantOperation; date: string; month: string; sites: StockSite[]; fetched_at?: string }

/* ------------------------------------------------------------------ */
/* plant_summary() (0065): the module dashboard                        */
/* ------------------------------------------------------------------ */

export type Rag = 'green' | 'amber' | 'red' | 'grey';

export interface SummarySite {
  site_id: string; name: string; code: string; last_day: string | null;
  made_today: number; rejected_today: number; good_today: number;
  made_mtd: number; rejected_mtd: number; good_mtd: number;
  reject_pct: number | null; reject_rag: Rag;
  target_month: number | null; target_to_date: number | null; target_basis: 'pro_rata' | 'full_month';
  working_days: number; working_days_elapsed: number;
  achievement_pct: number | null; achievement_rag: Rag;
  dispatched_today: number; dispatched_mtd: number; loads_today: number;
  on_hand: number; curing: number; available: number; short: number;
}
export interface SummaryProduct {
  product_id: string; code: string; name: string; category: string | null;
  per_pallet: number | null; per_m2: number | null; per_m: number | null;
  good_today: number; made_mtd: number; rejected_mtd: number; good_mtd: number;
  m2_mtd: number | null; m_mtd: number | null; reject_pct: number | null;
  dispatched_mtd: number; on_hand: number; available: number;
  target_month: number | null; target_to_date: number | null; achievement_pct: number | null; achievement_rag: Rag;
}
export interface PlantSummary {
  operation: PlantOperation; date: string; month: string;
  reject_amber_pct: number; reject_red_pct: number;
  working_days: number | null; working_days_elapsed: number | null; target_basis: string | null;
  totals: Omit<SummarySite, 'site_id' | 'name' | 'code' | 'last_day' | 'target_basis' | 'working_days' | 'working_days_elapsed'>;
  sites: SummarySite[];
  products: SummaryProduct[];
  by_reason: { reason: string; rejected: number }[];
  daily: { day: string; good: number; rejected: number }[];
  fetched_at?: string;
}
